"use client";

import * as React from "react";
import { cn } from "@workspace/ui/lib/utils";

/**
 * 드래그 리사이저 — UI 2.0 사이드바 폭/분할 조절 (JCG-2060 토대, E 에서 50/50 분할 재사용).
 *
 * - orientation="v": 세로 핸들. 사이드바 안쪽 경계에 절대배치. onResize 에 새 폭(px)을 전달
 *   (좌측 = clientX, 우측 = window.innerWidth - clientX).
 * - orientation="h": 가로 핸들. 항상 보이는 hairline + 그립. onResize 에 clientY 를 전달.
 * 호버/드래그 시 그립이 앰버로 강조된다.
 */
interface ResizeHandleProps {
  side?: "left" | "right";
  orientation?: "v" | "h";
  onResize: (value: number) => void;
  className?: string;
}

export default function ResizeHandle({
  side = "left",
  orientation = "v",
  onResize,
  className,
}: ResizeHandleProps) {
  const raf = React.useRef(0);
  const [dragging, setDragging] = React.useState(false);

  const start = React.useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      setDragging(true);

      const move = (ev: PointerEvent) => {
        cancelAnimationFrame(raf.current);
        raf.current = requestAnimationFrame(() => {
          if (orientation === "v") {
            onResize(side === "left" ? ev.clientX : window.innerWidth - ev.clientX);
          } else {
            onResize(ev.clientY);
          }
        });
      };
      const up = () => {
        setDragging(false);
        cancelAnimationFrame(raf.current);
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };

      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
      document.body.style.cursor = orientation === "v" ? "col-resize" : "row-resize";
      document.body.style.userSelect = "none";
    },
    [orientation, side, onResize],
  );

  React.useEffect(() => () => cancelAnimationFrame(raf.current), []);

  if (orientation === "h") {
    return (
      <div
        onPointerDown={start}
        role="separator"
        aria-orientation="horizontal"
        className={cn(
          "group relative my-1 flex h-3.5 w-full shrink-0 cursor-row-resize items-center justify-center",
          className,
        )}
      >
        <span className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-border" />
        <span
          className={cn(
            "relative z-[1] h-[5px] w-12 rounded-full transition-colors",
            dragging ? "bg-amber-500" : "bg-gray-300 dark:bg-gray-600 group-hover:bg-amber-500",
          )}
        />
      </div>
    );
  }

  return (
    <div
      onPointerDown={start}
      role="separator"
      aria-orientation="vertical"
      className={cn(
        "group absolute inset-y-0 z-10 flex w-2.5 cursor-col-resize items-center justify-center",
        side === "left" ? "right-0" : "left-0",
        className,
      )}
    >
      <span
        className={cn(
          "block h-10 w-[3px] rounded-full transition-colors",
          dragging ? "bg-amber-500" : "bg-transparent group-hover:bg-amber-500",
        )}
      />
    </div>
  );
}
